/**
 * Sentry error monitoring and session replay initialisation.
 *
 * Initialised eagerly at app startup (imported once in main.tsx before the
 * React tree mounts) so that errors thrown during the initial render are
 * still captured.
 *
 * Configuration notes:
 * - DSN is hard-coded (not in .env) so monitoring works in all environments
 *   without extra setup.
 * - `environment` is set from `import.meta.env.MODE` ('development' or 'production').
 * - `tracesSampleRate` is 1.0 in development (all transactions) and 0.2 in
 *   production to avoid exhausting Sentry quota on high traffic.
 * - Session replay captures 10% of sessions normally and 100% on error.
 * - The full React tree is wrapped in `<Sentry.ErrorBoundary>` inside App.tsx
 *   to catch unhandled errors and display a fallback UI.
 */
import * as Sentry from '@sentry/react';

Sentry.init({
  dsn: 'https://79a2d8bab9cc878276260752efc86399@o4511005558898688.ingest.de.sentry.io/4511005582884944',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: false,
    }),
  ],
  allowUrls: [/shareshack\.co\.uk/, /localhost/],
  tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
});

/**
 * Captures an error in Sentry, wrapping plain objects (e.g. Supabase
 * PostgrestError) into real Error instances so Sentry doesn't silently
 * drop them.
 */
export function captureSentryException(error: unknown): void {
  if (error instanceof Error) {
    Sentry.captureException(error);
  } else {
    const errorObj = error as Record<string, unknown> | null;
    const msg =
      typeof error === 'object' && errorObj !== null && 'message' in errorObj
        ? String(errorObj.message)
        : String(error);
    const wrapped = new Error(msg);
    // Attach the original object so Sentry includes its properties (code, details, hint).
    Sentry.captureException(wrapped, { extra: { originalError: error } });
  }
}
